import { useCallback, useEffect, useState } from 'react';
import { cloneTickOverrides } from '../../../utils/tickOverrides';
import { cloneDocVariables, docVariablesEqual } from '../utils/docVariablesUtils';

/** Record + propagate doc.variables changes (in-doc Variables table). */
export default function useDocVariablesCommit({
  docVariables,
  docVariablesRef,
  onDocVariablesChangeRef,
  historyRef,
  pushHistory,
  blocksRef,
  commit,
  tickOverridesRef,
}) {
  const [variablesHistoryEpoch, setVariablesHistoryEpoch] = useState(0);

  // Host may also update variables (load, create-from-picker before wrap).
  // Keep the ref aligned when props change and we aren't mid local edit
  // recording — undo/redo writes through applyDocVariables first.
  useEffect(() => {
    docVariablesRef.current = cloneDocVariables(docVariables);
  }, [docVariables, docVariablesRef]);

  const applyDocVariables = useCallback(
    (vars, { recordEpoch = false } = {}) => {
      const next = cloneDocVariables(vars);
      docVariablesRef.current = next;
      onDocVariablesChangeRef.current?.(next);
      if (recordEpoch) setVariablesHistoryEpoch((n) => n + 1);
    },
    [docVariablesRef, onDocVariablesChangeRef]
  );

  const commitDocVariables = useCallback(
    (vars, { coalesce = false, recordEpoch = false } = {}) => {
      if (!onDocVariablesChangeRef.current) return;
      const next = cloneDocVariables(vars);
      // Skip no-ops to avoid empty undo steps
      if (docVariablesEqual(docVariablesRef.current, next)) return;
      pushHistory(coalesce);
      applyDocVariables(next, { recordEpoch });
    },
    [pushHistory, applyDocVariables, docVariablesRef, onDocVariablesChangeRef]
  );

  /**
   * One undo step for a variable rename that also rewrites block references.
   * `undoDocVariables` is the pre-rename vars snapshot (old name, unre-written
   * nested refs) so Ctrl+Z / toast Undo restores the connected state.
   */
  const commitDocVariablesAndBlocks = useCallback(
    (vars, tree, { undoDocVariables = null } = {}) => {
      if (!onDocVariablesChangeRef.current) return;
      const nextVars = cloneDocVariables(vars);
      const h = historyRef.current;
      h.undo.push({
        blocks: blocksRef.current,
        docVariables: cloneDocVariables(
          undoDocVariables ?? docVariablesRef.current
        ),
        tickOverrides: cloneTickOverrides(tickOverridesRef?.current),
      });
      if (h.undo.length > 200) h.undo.shift();
      h.lastRecord = Date.now();
      h.redo = [];
      // Blocks first so pill rewrites land before resolveVariable identity
      // changes — otherwise useVariableRefresh can re-commit stale HTML
      // (old data-var-name) against the new resolver and undo the rename.
      if (tree && tree !== blocksRef.current) {
        commit(tree, { record: false });
      }
      applyDocVariables(nextVars, { recordEpoch: true });
    },
    [
      applyDocVariables,
      commit,
      historyRef,
      blocksRef,
      docVariablesRef,
      onDocVariablesChangeRef,
      tickOverridesRef,
    ]
  );

  return {
    applyDocVariables,
    commitDocVariables,
    commitDocVariablesAndBlocks,
    variablesHistoryEpoch,
  };
}
